import { AttachmentPreviewMethod } from 'owa-attachment-data';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc from 'owa-localize';
import { AddToCalendarSuccessMessgaeWithIcon } from 'owa-locstrings/lib/strings/addtocalendarsuccessmessgaewithicon.locstring.json';
import { AddToCalendarText } from 'owa-locstrings/lib/strings/addtocalendartext.locstring.json';
import { ICSFileErrorText } from 'owa-locstrings/lib/strings/icsfileerrortext.locstring.json';
import { ImportingText } from 'owa-locstrings/lib/strings/importingtext.locstring.json';
import { ClientItem, selectMailStoreItemById } from 'owa-mail-store';
import { AddToCalendarStatus, IcsPreviewState, lazyAddToCalendar } from 'owa-preview-ics';
import Item from 'owa-service/lib/contract/Item';
import { getOrCreateSxSStoreData, SxSStoreData } from 'owa-sxs-store';
import { PreviewPaneMode } from 'owa-sxsdata';
import {
    isMeetingMessage,
    isMeetingRequest,
    lazyAddItemToCalendar,
    MeetingMessageAddToCalendarStatus,
} from 'owa-meeting-message';

export default function getAddToCalendarButton(sxsId: string) {
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    let item: ClientItem = null;
    if (sxsStore.extendedViewState?.attachmentId) {
        item = selectMailStoreItemById(sxsStore.extendedViewState.attachmentId.Id) as ClientItem;
    }

    return new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: getAddToCalendarButtonLabel(sxsStore, item),
        [FilterableMenuItemArgs.icon]: ControlIcons.Add,
        [FilterableMenuItemArgs.onClick]: async () => {
            const icsPreviewState: IcsPreviewState = sxsStore.customPreviewState;
            if (item && isMeetingMessage(item) && !isMeetingRequest(item.ItemClass)) {
                lazyAddItemToCalendar.importAndExecute(item, sxsId);
            } else if (
                icsPreviewState &&
                icsPreviewState.addToCalendarStatus === AddToCalendarStatus.available
            ) {
                const addToCalendar = await lazyAddToCalendar.import();
                addToCalendar(
                    icsPreviewState,
                    null // callback
                );
            }
        },
        [FilterableMenuItemArgs.shouldShow]: () => {
            return isValidICSAttachment(sxsStore) || isValidMeetingAttachment(sxsStore, item);
        },
        [FilterableMenuItemArgs.disabled]:
            (sxsStore.customPreviewState &&
                (sxsStore.customPreviewState as IcsPreviewState).addToCalendarStatus !==
                    AddToCalendarStatus.available) ||
            sxsStore.meetingMessageAddToCalendarStatus !==
                MeetingMessageAddToCalendarStatus.Available,
    });
}

function getAddToCalendarButtonLabel(sxsStore: SxSStoreData, item: ClientItem): string {
    const icsPreviewState: IcsPreviewState = sxsStore.customPreviewState;
    if (!icsPreviewState && item && (!isMeetingMessage(item) || isMeetingRequest(item.ItemClass))) {
        return '';
    }

    if (icsPreviewState) {
        switch (icsPreviewState.addToCalendarStatus) {
            case AddToCalendarStatus.available:
                return loc(AddToCalendarText);
            case AddToCalendarStatus.adding:
                return loc(ImportingText);
            case AddToCalendarStatus.succeeded:
                return loc(AddToCalendarSuccessMessgaeWithIcon);
            case AddToCalendarStatus.failed:
                return loc(ICSFileErrorText);
            default:
                return '';
        }
    } else {
        switch (sxsStore.meetingMessageAddToCalendarStatus) {
            case MeetingMessageAddToCalendarStatus.Available:
                return loc(AddToCalendarText);
            case MeetingMessageAddToCalendarStatus.Adding:
                return loc(ImportingText);
            case MeetingMessageAddToCalendarStatus.Succeeded:
                return loc(AddToCalendarSuccessMessgaeWithIcon);
            case MeetingMessageAddToCalendarStatus.Failed:
                return loc(ICSFileErrorText);
            default:
                return '';
        }
    }
}

function isValidICSAttachment(sxsStore: SxSStoreData): boolean {
    return (
        sxsStore.viewState?.previewPane &&
        sxsStore.viewState.previewPane.mode === PreviewPaneMode.Custom &&
        sxsStore.extendedViewState &&
        sxsStore.extendedViewState.previewMode === AttachmentPreviewMethod.CalendarEvent &&
        sxsStore.customPreviewState &&
        (sxsStore.customPreviewState as IcsPreviewState).addToCalendarStatus !==
            AddToCalendarStatus.disabled
    );
}

function isValidMeetingAttachment(sxsStore: SxSStoreData, item: ClientItem): boolean {
    return (
        isFeatureEnabled('doc-itemAttachment-to-calendar') &&
        sxsStore.viewState?.previewPane &&
        sxsStore.viewState.previewPane.mode === PreviewPaneMode.Custom &&
        sxsStore.extendedViewState &&
        sxsStore.extendedViewState.previewMode === AttachmentPreviewMethod.ItemAttachment &&
        item &&
        !!isMeetingMessage(item as Item) &&
        !isMeetingRequest(item.ItemClass)
    );
}
